import { EU_STORES, ReebeloStoreT } from '@lambda/reebelo';
import type { VendorDetails } from '@lambda/apis/src/dynamoDb/types';
import { COUNTRIES } from '@/utils/constants/countries';

type CompanyT = VendorDetails['company'];

/**
 * Legal block at the bottom of the seller popup (RT-17188): legal name and
 * full address on UK, EU and US stores. Keyed on EU_STORES so a new EU market
 * inherits it.
 */
export const showsLegalDetails = (store: ReebeloStoreT): boolean =>
  store === 'reebelo-uk' || store === 'reebelo-us' || EU_STORES.includes(store);

/** Registration number inside that block: UK and EU only, never US. */
export const showsRegistrationNumber = (store: ReebeloStoreT): boolean =>
  store === 'reebelo-uk' || EU_STORES.includes(store);

export const resolveGoodReviewsPercentage = (
  reviewsCount: number | null,
  goodReviewsPercent: number | null,
): number | null => {
  if (reviewsCount == null) {
    return null;
  }

  return reviewsCount > 0 ? goodReviewsPercent : 100;
};

/**
 * "address1, address2, country" with empty parts dropped — the same text the
 * UK block prints today.
 */
export const formatCompanyAddress = (
  company: Pick<CompanyT, 'address1' | 'address2' | 'country'>,
): string =>
  [company.address1, company.address2, company.country]
    .filter(Boolean)
    .join(', ');

/**
 * Cobalt stores `company.country` as an English name picked from its country
 * list (see `@/utils/constants/countries`), so that list is what resolves the
 * name to an ISO 3166-1 alpha-2 code. Matched trimmed and case-insensitively,
 * so a record whose casing differs from the list still resolves.
 */
const findCountry = (countryName: string) => {
  const match = countryName.trim().toLowerCase();

  return COUNTRIES.find((c) => c.name.toLowerCase() === match) ?? null;
};

/** The vendor's country as an ISO code, for the flag beside its name. */
export const getCountryCode = (
  countryName: string | null | undefined,
): string | null =>
  countryName ? findCountry(countryName)?.code ?? null : null;

/**
 * English stores show the stored name in the list's own spelling. Other stores
 * ask ICU for the region name in the store language. An unknown name falls
 * back to the stored one, so the popup never renders blank.
 */
export const localizeCountryName = (
  countryName: string,
  locale: string,
): string => {
  if (!countryName) {
    return countryName;
  }

  const country = findCountry(countryName);

  if (!country) {
    return countryName;
  }

  if (locale === 'en') {
    return country.name;
  }

  try {
    return (
      new Intl.DisplayNames([locale], { type: 'region' }).of(country.code) ??
      country.name
    );
  } catch {
    return country.name;
  }
};
